import { consola } from 'consola'
import { ref } from 'vue'
import { useAnalytics } from '@shared/lib'
import { resolveBranchAliases } from '@entities/product'
import { useCartStore } from '@entities/order'
import { useInstitutionStore } from '@entities/institution'
import { useUserProfileStore } from '@entities/user'
import type { Order } from '@entities/order'

const logger = consola.withTag('repeat-order')

/** Нормализованная запись меню для повтора заказа (single-товар или вариант группы) */
interface MenuEntry {
  id: number | string
  name: string
  price: number
  img: Array<{ key: string; url: string }> | null
  stopList: boolean
  categoryId?: number
}

export type RepeatOrderMode = 'replace' | 'append'

/** Причина, по которой позицию не удалось перенести в корзину */
export type RepeatSkipReason = 'unavailable' | 'stop_list' | 'combo_no_data'

export interface RepeatSkippedItem {
  name: string
  reason: RepeatSkipReason
}

export interface RepeatOrderResult {
  /** Сколько позиций (с учётом количества) успешно добавлено */
  addedCount: number
  /** Позиции, которые не удалось перенести */
  skipped: RepeatSkippedItem[]
}

// modifiers — кастомизация слотов комбо; при повторе заказа переносим как есть
type ComboSelections = Array<{
  group_id: string | number
  product_ids: (string | number)[]
  modifiers?: Array<{ product_id: number; addons?: number[]; excludes?: number[] }>
}>

/** Признак подарка (тип заказа не моделирует это поле — проверяем защитно) */
function isGiftItem(item: any): boolean {
  if (item?.is_gift || item?.isGift || item?.gift || item?.is_present) return true
  // Бесплатная позиция (и за единицу, и итог) — почти наверняка подарок
  const unit = Number(item?.unitPrice ?? item?.unit_price ?? Number.NaN)
  const total = Number(item?.totalPrice ?? item?.total_price ?? Number.NaN)
  return unit === 0 && total === 0
}

/** Признак комбо (см. варианты полей в useCartStore.updateCartFromServerResponse) */
function isComboItem(item: any): boolean {
  return item?.item_type === 'combo' || !!item?.comboId || !!item?.combo_id
}

/** Достаёт выборы комбо из любого варианта поля */
function getComboSelections(item: any): ComboSelections | null {
  const raw = item?.comboSelections || item?.combo_selections || item?.selections
  return Array.isArray(raw) && raw.length > 0 ? raw : null
}

/** Приводит массив id (string|number) к number[] */
function toNumberIds(ids: unknown): number[] {
  if (!Array.isArray(ids)) return []
  return ids.map(Number).filter(n => Number.isFinite(n))
}

/** Название позиции для сообщений */
function getItemName(item: any): string {
  return item?.productName || item?.name || item?.product?.name || 'Позиция'
}

export function useRepeatOrder() {
  const cartStore = useCartStore()
  const institutionStore = useInstitutionStore()
  const userProfileStore = useUserProfileStore()

  const isRepeating = ref(false)

  /** Индексирует варианты группового товара в карту меню */
  function indexGroupVariants(map: Map<string, MenuEntry>, product: any, categoryId?: number): void {
    for (const variant of product.variants) {
      if (variant?.id == null) continue
      map.set(String(variant.id), {
        id: variant.id,
        name: variant.name ?? product.name,
        price: Number(variant.price) || 0,
        img: variant.img ?? product.img ?? null,
        stopList: variant.stop_list === true,
        categoryId,
      })
    }
  }

  /** Индексирует одиночный товар в карту меню */
  function indexSingle(map: Map<string, MenuEntry>, product: any, categoryId?: number): void {
    map.set(String(product.id), {
      id: product.id,
      name: product.name,
      price: Number(product.price) || 0,
      img: product.img ?? null,
      stopList: product.stop_list === true || product.is_stopped === true || product.is_active === false,
      categoryId,
    })
  }

  /**
   * Плоская карта меню по строковому id. Индексирует одиночные товары и
   * варианты групп (заказ ссылается на конкретный id варианта/товара).
   */
  function buildProductMap(): Map<string, MenuEntry> {
    const map = new Map<string, MenuEntry>()
    for (const category of institutionStore.products || []) {
      for (const product of (category?.products || []) as any[]) {
        const categoryId = product?.category_id ?? (category as any)?.category_id
        if (product?.type === 'group' && Array.isArray(product.variants)) {
          indexGroupVariants(map, product, categoryId)
        } else if (product?.id != null && product?.type !== 'combo') {
          indexSingle(map, product, categoryId)
        }
      }
    }
    return map
  }

  /** Добавляет комбо из данных заказа; возвращает true при успехе */
  async function addComboFromOrder(item: any): Promise<boolean> {
    const selections = getComboSelections(item)
    const comboId = item.comboId || item.combo_id
    if (!selections || !comboId) return false

    await cartStore.addComboToCart({
      combo_id: comboId,
      name: getItemName(item),
      price: Number(item.totalPrice ?? item.total_price ?? item.price ?? 0),
      img: Array.isArray(item.img) ? item.img : undefined,
      selections,
      base_price: item.base_price,
      discount_percent: item.discount_percent,
    })
    return true
  }

  /** Добавляет обычный товар по записи меню */
  async function addRegularProduct(item: any, entry: MenuEntry, branchId: number, quantity: number): Promise<void> {
    await cartStore.addToCart(
      {
        id: entry.id,
        name: getItemName(item) || entry.name,
        price: entry.price,
        img: entry.img,
        category_id: entry.categoryId,
        addons: toNumberIds(item.addons),
        excludes: toNumberIds(item.excludes),
        quantity,
      } as any,
      branchId,
    )

    // Добавление идёт мимо карточки товара — шаг воронки размечаем здесь,
    // иначе «Повторить заказ» выглядел бы в отчётах как пустая сессия.
    useAnalytics().track('add_to_cart', {
      productId: Number(entry.id) || 0,
      productName: getItemName(item) || entry.name,
      categoryId: entry.categoryId,
      quantity,
      total: entry.price * quantity,
      price: entry.price,
      source: 'repeat_order',
    })
  }

  /**
   * Карточки блюд текущего филиала для позиций заказа, которых нет в его меню.
   *
   * Заказ мог быть сделан на другой точке, а карточка товара принадлежит филиалу
   * (свой id и артикул кассы), поэтому по id из истории блюдо в меню не находится.
   * Сервер приводит id к карточкам этого филиала через группы «одно блюдо»
   * (POST /products/resolve-for-branch): вернувшийся id ищем в меню — там есть
   * картинки и категория; если карточки в меню нет, собираем запись из ответа.
   *
   * @returns карта «id из заказа → запись меню этого филиала»
   */
  async function buildAliasMap(
    order: Order,
    productMap: Map<string, MenuEntry>,
    branchId: number,
  ): Promise<Map<string, MenuEntry>> {
    const aliasMap = new Map<string, MenuEntry>()

    const foreignIds = (order.items as any[])
      .filter(item => !isGiftItem(item) && !isComboItem(item))
      .map(item => String(item.productId ?? item.product_id ?? item.id ?? ''))
      .filter(id => id && !productMap.has(id))

    if (foreignIds.length === 0) return aliasMap

    // Общий резолв entities/product: сбой сети внутри — позиции просто
    // останутся «недоступны», как было до групп блюд
    const resolved = await resolveBranchAliases(foreignIds, branchId)

    for (const entry of resolved) {
      if (!entry.product_id) continue

      const fromMenu = productMap.get(entry.product_id)
      aliasMap.set(
        entry.requested,
        fromMenu ?? {
          id: entry.product_id,
          name: entry.name,
          price: Number(entry.price) || 0,
          img: null,
          stopList: entry.stop_list === true || entry.available === false,
        },
      )
    }

    logger.info('resolved foreign products for branch', {
      requested: foreignIds.length,
      resolved: aliasMap.size,
    })

    return aliasMap
  }

  /** Обрабатывает одну позицию заказа, мутируя result */
  async function processItem(
    item: any,
    productMap: Map<string, MenuEntry>,
    aliasMap: Map<string, MenuEntry>,
    branchId: number,
    result: RepeatOrderResult,
  ): Promise<void> {
    const itemName = getItemName(item)
    const quantity = Number(item.quantity) || 1

    // 1. Подарки не переносим
    if (isGiftItem(item)) {
      logger.debug('skip gift', itemName)
      return
    }

    // 2. Комбо
    if (isComboItem(item)) {
      try {
        const ok = await addComboFromOrder(item)
        if (ok) result.addedCount += quantity
        else result.skipped.push({ name: itemName, reason: 'combo_no_data' })
      } catch (error) {
        logger.warn('failed to add combo', itemName, error)
        result.skipped.push({ name: itemName, reason: 'combo_no_data' })
      }
      return
    }

    // 3. Обычный товар — ищем в меню текущего филиала, а если это карточка другого
    // филиала (заказ сделан на другой точке) — среди резолвнутых сервером аналогов.
    const productId = String(item.productId ?? item.product_id ?? item.id ?? '')
    const entry = productMap.get(productId) ?? aliasMap.get(productId)
    if (!entry) {
      result.skipped.push({ name: itemName, reason: 'unavailable' })
      return
    }
    if (entry.stopList) {
      result.skipped.push({ name: itemName || entry.name, reason: 'stop_list' })
      return
    }

    try {
      await addRegularProduct(item, entry, branchId, quantity)
      result.addedCount += quantity
    } catch (error) {
      logger.warn('failed to add product', itemName, error)
      result.skipped.push({ name: itemName || entry.name, reason: 'unavailable' })
    }
  }

  /**
   * Переносит заказ в корзину.
   *
   * - Подарочные позиции пропускаются молча (начисляются автоматически акцией/лояльностью).
   * - Комбо переносятся через addComboToCart, если в данных заказа есть выборы (selections).
   * - Обычные товары ищутся в актуальном меню текущего филиала: недоступные/стоп-лист
   *   пропускаются и попадают в skipped, цена берётся актуальная из меню.
   *
   * @param order заказ для повтора
   * @param mode 'replace' — очистить корзину перед добавлением; 'append' — добавить к текущим
   */
  async function repeatOrder(order: Order | null, mode: RepeatOrderMode = 'append'): Promise<RepeatOrderResult> {
    const result: RepeatOrderResult = { addedCount: 0, skipped: [] }

    if (!order || !Array.isArray(order.items) || order.items.length === 0) {
      logger.warn('repeatOrder: empty order')
      return result
    }

    const branchId = institutionStore.selectedBranch?.id
    if (!branchId) {
      throw new Error('Не выбран филиал')
    }

    isRepeating.value = true
    try {
      // Меню могло быть не загружено (заход напрямую в модалку) — или загружено для
      // ДРУГОГО филиала, если его сменили в этой же сессии. Повтор собирает позиции по
      // меню, поэтому чужое меню подставило бы карточки не той точки: см. тот же гард
      // в useOrderedProducts.
      const menuBranchId = institutionStore.productsBranchId
      if (!institutionStore.products?.length || Number(menuBranchId) !== Number(branchId)) {
        await institutionStore.fetchProducts(branchId, userProfileStore.getUserId || undefined)
      }

      if (mode === 'replace') {
        await cartStore.clearCart()
      }

      const productMap = buildProductMap()
      const aliasMap = await buildAliasMap(order, productMap, branchId)

      for (const item of order.items as any[]) {
        await processItem(item, productMap, aliasMap, branchId, result)
      }

      logger.success('repeatOrder done', { addedCount: result.addedCount, skipped: result.skipped.length })
      return result
    } finally {
      isRepeating.value = false
    }
  }

  return {
    isRepeating,
    repeatOrder,
  }
}
